import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from "react";

type AinaAnchorRegistryValue = {
  registerDashboardAinaAnchor: (el: HTMLElement | null) => void;
  registerBottomNavAinaAnchor: (el: HTMLElement | null) => void;
  getFallbackAinaAnchor: () => HTMLElement | null;
};

const AinaAnchorRegistryContext = createContext<AinaAnchorRegistryValue | null>(
  null,
);

export function AinaAnchorRegistryProvider({
  children,
}: {
  children: ReactNode;
}) {
  const dashboardRef = useRef<HTMLElement | null>(null);
  const bottomNavRef = useRef<HTMLElement | null>(null);

  const registerDashboardAinaAnchor = useCallback((el: HTMLElement | null) => {
    dashboardRef.current = el;
  }, []);

  const registerBottomNavAinaAnchor = useCallback((el: HTMLElement | null) => {
    bottomNavRef.current = el;
  }, []);

  const getFallbackAinaAnchor = useCallback((): HTMLElement | null => {
    if (typeof window === "undefined") return null;
    const bottomNavLayout = window.matchMedia("(max-width: 1024px)").matches;
    return bottomNavLayout ? bottomNavRef.current : dashboardRef.current;
  }, []);

  const value = useMemo(
    () => ({
      registerDashboardAinaAnchor,
      registerBottomNavAinaAnchor,
      getFallbackAinaAnchor,
    }),
    [
      registerDashboardAinaAnchor,
      registerBottomNavAinaAnchor,
      getFallbackAinaAnchor,
    ],
  );

  return (
    <AinaAnchorRegistryContext.Provider value={value}>
      {children}
    </AinaAnchorRegistryContext.Provider>
  );
}

export function useAinaAnchorRegistry(): AinaAnchorRegistryValue {
  const ctx = useContext(AinaAnchorRegistryContext);
  if (!ctx) {
    throw new Error(
      "useAinaAnchorRegistry must be used within AinaAnchorRegistryProvider",
    );
  }
  return ctx;
}
